import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import api from '../services/api';
import navigationRef from '../utils/navigationRef';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check localStorage on mount and set user state
    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            
            if (token && storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                    setIsAuthenticated(true);
                } catch (error) {
                    console.error('Error parsing user:', error);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } else {
                setUser(null);
                setIsAuthenticated(false);
            }
            setLoading(false);
        };
        
        checkAuth();
    }, []);

    // ✅ NEW: Refresh user data from API
    const refreshUser = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return null;
            
            const response = await api.get('/auth/profile');
            if (response.data) {
                setUser(response.data);
                localStorage.setItem('user', JSON.stringify(response.data));
                return response.data;
            }
        } catch (error) {
            console.error('Error refreshing user:', error);
            // If unauthorized, clear session
            if (error.response?.status === 401) {
                logout();
            }
        }
        return null;
    }, []);

    const register = useCallback(async (name, email, password) => {
        try {
            const data = await authService.register({ name, email, password });
            if (data?.token) {
                setUser(data);
                setIsAuthenticated(true);
                return { success: true, data };
            }
            return { success: false, error: 'Registration failed - No token received' };
        } catch (error) {
            console.error('Registration error:', error);
            return { 
                success: false, 
                error: error.response?.data?.message || 'Registration failed. Please try again.' 
            };
        }
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const data = await authService.login({ email, password });
            
            if (data?.token) {
                setUser(data);
                setIsAuthenticated(true);
                return { success: true, data };
            }
            return { success: false, error: 'Login failed - No token received' };
        } catch (error) {
            console.error('Login error:', error);
            return { 
                success: false, 
                error: error.response?.data?.message || 'Login failed. Please check your credentials.' 
            };
        }
    }, []);

    // authService.logout() never throws (its own API call is best-effort,
    // wrapped in a try/catch) — awaiting it here just avoids a race between
    // clearing React state and clearing localStorage; existing callers that
    // call logout() without awaiting are unaffected.
    const logout = useCallback(async () => {
        await authService.logout();
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    // OTP verify replaces the token, then re-fetches the full profile so we
    // don't overwrite the stored user with the OTP endpoint's trimmed response.
    const verifyOtp = useCallback(async (email, otp) => {
        try {
            const data = await authService.verifyOtp(email, otp);
            if (data?.token) {
                const freshUser = await refreshUser();
                setIsAuthenticated(true);
                return { success: true, data: freshUser || data };
            }
            return { success: false, error: 'Verification failed - No token received' };
        } catch (error) {
            console.error('Verify OTP error:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Invalid or expired OTP. Please try again.'
            };
        }
    }, [refreshUser]);

    const resendOtp = useCallback(async (email) => {
        try {
            const data = await authService.resendOtp(email);
            return { success: true, data };
        } catch (error) {
            console.error('Resend OTP error:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to resend OTP. Please try again.'
            };
        }
    }, []);

    // For "login-at-checkout" buttons (Buy, Start Test, etc.) added in later phases:
    // returns true if already logged in; otherwise sends the user to /login,
    // remembering returnTo via a query param so login can send them back.
    const requireAuth = useCallback((returnTo) => {
        if (isAuthenticated) return true;

        const target = returnTo ? `/login?redirect=${encodeURIComponent(returnTo)}` : '/login';
        if (navigationRef.current) {
            navigationRef.current(target);
        } else {
            // Defensive fallback — should not happen once the app has mounted.
            window.location.href = target;
        }
        return false;
    }, [isAuthenticated]);

    const value = {
        user,
        loading,
        isAuthenticated,
        register,
        login,
        logout,
        refreshUser, // ✅ EXPORT refreshUser
        verifyOtp,
        resendOtp,
        requireAuth,
        isVerified: user?.isVerified === true,
        isAdmin: user?.role === 'admin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};